import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { UserPlus, Mail, Crown, Edit2, Trash2, RefreshCw, X, Clock, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { config } from '../../config/environment';

const BACKEND_URL = config.backendUrl;

const TeamManagement = () => {
  const { user, tenantId, role } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editRoleModalOpen, setEditRoleModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  
  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [inviting, setInviting] = useState(false);
  
  const isOwner = role === 'Owner';

  useEffect(() => {
    fetchAvailableRoles();
  }, []);

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchAvailableRoles = async () => {
    try {
      const response = await api.get(`${BACKEND_URL}/api/v1/admin/roles`);
      if (response.success) {
        const roles = response.roles
          .filter(r => r.role_name !== 'Owner')
          .map(r => ({ id: r.role_id, name: r.role_name, description: r.description }));
        setAvailableRoles(roles);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      const membersResponse = await api.get(`${BACKEND_URL}/api/v1/users/team`);
      setTeamMembers(membersResponse);
      if (isOwner) {
        const invitationsResponse = await api.get(`${BACKEND_URL}/api/v1/users/invitations`);
        setPendingInvitations(invitationsResponse);
      }
    } catch (error) {
      console.error('Error fetching team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail || !inviteRole) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      setInviting(true);
      const response = await api.post(`${BACKEND_URL}/api/v1/users/invite`, { email: inviteEmail, role_id: inviteRole });
      const invitationUrl = response.invitation_url;
      if (navigator.clipboard && invitationUrl) {
        await navigator.clipboard.writeText(invitationUrl);
        toast.success(`Invitation created! Link copied to clipboard.`);
      }
      setInviteEmail('');
      setInviteRole('');
      setInviteModalOpen(false);
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateRole = async (userId, newRoleId) => {
    try {
      await api.put(`${BACKEND_URL}/api/v1/users/team/${userId}/role`, { role_id: newRoleId });
      toast.success('User role updated');
      setEditRoleModalOpen(false);
      setSelectedMember(null);
      fetchTeamData();
    } catch (error) {
      toast.error(error.detail || 'Failed to update role');
    }
  };

  const handleRemove = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName} from the team?`)) return;
    try {
      await api.delete(`${BACKEND_URL}/api/v1/users/team/${userId}`);
      toast.success(`${userName} removed`);
      fetchTeamData();
    } catch (error) {
      toast.error('Failed to remove user');
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm('Cancel this invitation?')) return;
    try {
      await api.delete(`${BACKEND_URL}/api/v1/users/invitations/${invitationId}`);
      toast.success('Invitation cancelled');
      fetchTeamData();
    } catch (error) {
      toast.error('Failed to cancel invitation');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getExpiresIn = (expiresAt) => {
    const diff = new Date(expiresAt) - new Date();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    if (days < 0) return 'Expired';
    if (days === 0) return 'Today';
    return `${days}d`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Team Members Section - Compact */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="p-3 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm font-semibold text-white">Team Members</CardTitle>
              <CardDescription className="text-xs text-gray-400 mt-0.5">
                Manage your team and their access levels
              </CardDescription>
            </div>
            {isOwner && (
              <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
                <DialogTrigger asChild>
                  <Button className="h-7 px-3 text-xs bg-blue-600 hover:bg-blue-700">
                    <UserPlus className="w-3 h-3 mr-1" />
                    Invite User
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md bg-gray-800 border-gray-700">
                  <DialogHeader>
                    <DialogTitle className="text-sm text-white">Invite Team Member</DialogTitle>
                    <DialogDescription className="text-xs text-gray-400">
                      Send an invitation to add a new member
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleInvite} className="space-y-3 mt-3">
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase text-gray-400">Email Address *</Label>
                      <Input
                        type="email"
                        placeholder="user@example.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        className="h-8 text-xs bg-gray-900 border-gray-600 text-white"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase text-gray-400">Role *</Label>
                      <select
                        className="w-full h-8 px-2 text-xs bg-gray-900 border border-gray-600 rounded text-white"
                        value={inviteRole}
                        onChange={(e) => setInviteRole(e.target.value)}
                        required
                      >
                        <option value="">Select a role</option>
                        {availableRoles.map((role) => (
                          <option key={role.id} value={role.id}>{role.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => setInviteModalOpen(false)} className="h-7 px-3 text-xs">Cancel</Button>
                      <Button type="submit" disabled={inviting} className="h-7 px-3 text-xs bg-blue-600 hover:bg-blue-700">
                        {inviting ? 'Sending...' : 'Send'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-gray-900">
                <TableRow className="border-gray-700">
                  <TableHead className="px-3 py-2 text-[10px] uppercase text-gray-400">User</TableHead>
                  <TableHead className="px-3 py-2 text-[10px] uppercase text-gray-400">Email</TableHead>
                  <TableHead className="px-3 py-2 text-[10px] uppercase text-gray-400">Role</TableHead>
                  <TableHead className="px-3 py-2 text-[10px] uppercase text-gray-400">Joined</TableHead>
                  <TableHead className="px-3 py-2 text-[10px] uppercase text-gray-400">Last Login</TableHead>
                  {isOwner && <TableHead className="px-3 py-2 text-[10px] uppercase text-gray-400 text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {teamMembers.map((member) => (
                  <TableRow key={member.user_id} className="border-gray-700 hover:bg-gray-800/50">
                    <TableCell className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-[10px] font-medium">
                          {member.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </div>
                        <span className="text-xs text-white font-medium">{member.name}</span>
                        {member.is_current_user && <Badge variant="outline" className="text-[10px] h-4 px-1">You</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="px-3 py-2 text-xs text-gray-400">{member.email}</TableCell>
                    <TableCell className="px-3 py-2">
                      <Badge variant={member.is_owner ? "default" : "secondary"} className={`text-[10px] h-5 ${member.is_owner ? "bg-yellow-600" : "bg-gray-700"}`}>
                        {member.is_owner && <Crown className="w-2.5 h-2.5 mr-0.5" />}
                        {member.role_name}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-3 py-2 text-xs text-gray-400">{formatDate(member.joined_at)}</TableCell>
                    <TableCell className="px-3 py-2 text-xs text-gray-400">{member.last_login ? formatDate(member.last_login) : 'Never'}</TableCell>
                    {isOwner && (
                      <TableCell className="px-3 py-2 text-right">
                        {!member.is_owner && !member.is_current_user ? (
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setSelectedMember(member); setEditRoleModalOpen(true); }}>
                              <Edit2 className="w-3 h-3 text-gray-400" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-300" onClick={() => handleRemove(member.user_id, member.name)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : <span className="text-xs text-gray-500">—</span>}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pending Invitations - Compact */}
      {isOwner && pendingInvitations.length > 0 && (
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="p-3 border-b border-gray-700">
            <CardTitle className="text-sm font-semibold text-white">Pending Invitations</CardTitle>
            <CardDescription className="text-xs text-gray-400">Invitations waiting acceptance</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader className="bg-gray-900">
                <TableRow className="border-gray-700">
                  <TableHead className="px-3 py-2 text-[10px] uppercase text-gray-400">Email</TableHead>
                  <TableHead className="px-3 py-2 text-[10px] uppercase text-gray-400">Role</TableHead>
                  <TableHead className="px-3 py-2 text-[10px] uppercase text-gray-400">Expires</TableHead>
                  <TableHead className="px-3 py-2 text-[10px] uppercase text-gray-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingInvitations.map((inv) => (
                  <TableRow key={inv.invitation_id} className="border-gray-700">
                    <TableCell className="px-3 py-2 text-xs text-white">{inv.email}</TableCell>
                    <TableCell className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{inv.role_name}</Badge></TableCell>
                    <TableCell className="px-3 py-2 text-xs text-gray-400">{getExpiresIn(inv.expires_at)}</TableCell>
                    <TableCell className="px-3 py-2 text-right">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => handleCancelInvitation(inv.invitation_id)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Edit Role Modal */}
      {selectedMember && (
        <Dialog open={editRoleModalOpen} onOpenChange={setEditRoleModalOpen}>
          <DialogContent className="sm:max-w-md bg-gray-800 border-gray-700">
            <DialogHeader>
              <DialogTitle className="text-sm text-white">Change Role</DialogTitle>
              <DialogDescription className="text-xs text-gray-400">Update role for {selectedMember.name}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-3">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase text-gray-400">Current Role</Label>
                <div className="p-2 bg-gray-900 rounded"><Badge className="text-[10px]">{selectedMember.role_name}</Badge></div>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase text-gray-400">New Role</Label>
                <select
                  className="w-full h-8 px-2 text-xs bg-gray-900 border border-gray-600 rounded text-white"
                  onChange={(e) => handleUpdateRole(selectedMember.user_id, e.target.value)}
                  defaultValue=""
                >
                  <option value="">Select new role</option>
                  {availableRoles.map((role) => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default TeamManagement;
